import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useField, useFormikContext } from 'formik';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';
import { mix, rgba } from 'polished';

import { Button, Paragraph } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

const IconDot = styled(Box)`
	width: 20px;
	height: 20px;
	border-radius: 12px;
	background: ${(props) => props.theme.colors.lightBlue};
`;

const IconContainer = styled(Flex)`
	width: 50px;
	height: 50px;
	border-radius: 25px;
	border: 1px solid ${(props) => props.theme.colors.lightBlueDark};
	justify-content: center;
	align-items: center;
	flex-grow: 0;
	flex-shrink: 0;
`;

const OptionContainerNormal = css`
	cursor: pointer;

	&:hover {
		${IconContainer} {
			background: ${(props) => rgba(props.theme.colors.lightBlue, 0.25)};
			border: 2px solid
				${(props) => mix(0.25, props.theme.colors.slateBlue, props.theme.colors.lightBlueDark)};
		}

		${IconDot} {
			background: ${(props) =>
				mix(0.25, props.theme.colors.slateBlue, props.theme.colors.lightBlue)};
		}

		&:active {
			${IconContainer} {
				border: 2px solid
					${(props) => mix(0.5, props.theme.colors.slateBlue, props.theme.colors.lightBlueDark)};
			}

			${IconDot} {
				background: ${(props) =>
					mix(0.5, props.theme.colors.slateBlue, props.theme.colors.lightBlue)};
			}
		}
	}
`;

const OptionContainerSelected = css`
	${IconContainer} {
		border: none;
		background: ${(props) => props.theme.colors.employee};
	}

	${IconDot} {
		background: rgba('black', 0.25);
	}
`;

const OptionContainer = styled(Flex)<{ selected?: boolean }>`
	${(props) => (props.selected ? OptionContainerSelected : OptionContainerNormal)}
`;

function Icon() {
	return (
		<IconContainer mr={[3, 3, 0]}>
			<IconDot />
		</IconContainer>
	);
}

export default function PickOne(props: { type: graphql.EngagementType }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const formik = useFormikContext();
	const [{ value }, , { setValue }] = useField('answer.index');
	const [loading, setLoading] = useState(false);

	return (
		<Box width={['100%', '100%', 'auto']}>
			<Flex px={32} mt={3} flexDirection={['column', 'column', 'row']}>
				{props.type.options!.map((option, i) => (
					<Box key={i}>
						<OptionContainer
							alignItems={['center', 'center', 'auto']}
							flexDirection={['row', 'row', 'column']}
							ml={[0, 0, i === 0 ? 0 : 5]}
							mt={[i === 0 ? 0 : 2, i === 0 ? 0 : 2, 0]}
							width={['auto', 'auto', 64]}
							selected={value === i}
							onClick={() => {
								if (!loading) {
									setValue(i);
								}
							}}
						>
							<Icon />
							<Paragraph
								width={['auto', 'auto', 96]}
								textAlign={['inherit', 'center']}
								mt={[0, 0, 2]}
							>
								{localize(option!.title ?? []).text}
							</Paragraph>
						</OptionContainer>
					</Box>
				))}
			</Flex>
			{value !== undefined && (
				<Flex justifyContent="center" mt={4} display={['block', 'flex', 'flex']} px={[32, 32, 0]}>
					<Button
						loading={loading}
						title={t('engagement.respond.pickOne.confirm')}
						onClick={async () => {
							setLoading(true);
							await formik.submitForm();
						}}
					/>
				</Flex>
			)}
		</Box>
	);
}
